import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import fields from '../../lib/fields';
import FormFieldsCompact from '../../components/form/fieldsCompact';
import ImageScroller from '../../components/imagescroller';
import DateSelector from './dateSelector';
import CourseSignupCard from './courseSignupCard';

import localUtil from '../../lib/util';
import { countCourseSignups } from '../../lib/courseSignups';
import { courseExamAccess, COURSE_EXAM_STATUS, formatDateTime } from '../../lib/examResults';

@observer
class CourseView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            showEdit: false,
            sections: fields.get('course'),
            search: '',
            searchResults: [],
            searchLoading: false,
        };
        this.searchTimer = null;
    }

    loadAll = async (props = this.props) => {
        const { id } = props;
        const { courseStore } = this.props.stores;

        await courseStore.load(id, false, {
            addData: ['tags'],
            query: {
                hostname: courseStore.currentHostname,
            },
        });
        await this.loadParticipantExam();
    }

    // The exam sent to this participant, if any. A user who was not invited gets
    // nothing back from the API, so the store keeps whatever exam it had; render
    // only trusts it when its id matches the course's exam.
    loadParticipantExam = async () => {
        const { courseStore, examStore, examAttemptStore, userStore } = this.props.stores;
        const { course = {} } = courseStore;
        const { user = {} } = userStore;
        if (!(course.exam > 0) || !(user.id > 0)) {
            return;
        }
        await examStore.loadExam(course.exam);
        await examAttemptStore.loadMyAttempts();
    }

    toggleEdit = () => {
        this.setState({ showEdit: !this.state.showEdit });
    }

    editCourse = () => {
        const { appState, courseStore } = this.props.stores;
        const { course } = courseStore;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightLarge } = appState;

        appState.openDrawer('editCourse', {
            id: course.id,
            height: drawerHeightLarge,
            callback: () => {
                appState.toggleDrawer();
                this.loadAll();
            },
        }, drawerLevel + 1);
    }

    sendExam = () => {
        const { appState, courseStore } = this.props.stores;
        const { course } = courseStore;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMedium } = appState;

        appState.openDrawer('sendCourseExam', {
            id: course.id,
            height: drawerHeightMedium,
            callback: () => {
                appState.toggleDrawer();
                this.loadAll();
            },
        }, drawerLevel + 1);
    }

    copyCourse = () => {
        const { appState, courseStore } = this.props.stores;
        const { course } = courseStore;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightLarge } = appState;

        // Copy course details, excluding participants, id, counters, and generated fields
        const copiedCourse = {
            title: course.title ? `${course.title} (kopi)` : '',
            ingress: course.ingress || '',
            body: course.body || '',
            type: course.type || '',
            active: 1,
            public: course.public || 0,
            registrationIsOpen: course.registrationIsOpen || false,
            selfSignup: course.selfSignup !== undefined ? course.selfSignup : true,
            registrationStartDate: course.registrationStartDate || null,
            registrationEndDate: course.registrationEndDate || null,
            slots: course.slots || 0,
            price: course.price || 0,
            vat: course.vat || 25,
            address: course.address || '',
            postalcode: course.postalcode || '',
            place: course.place || '',
            country: course.country || '',
            url: course.url || '',
            signupFields: course.signupFields ? JSON.parse(JSON.stringify(course.signupFields)) : [],
            dates: course.dates ? course.dates.map(d => ({
                startTime: d.startTime,
                endTime: d.endTime,
                slots: d.slots || 0,
                title: d.title || '',
                price: d.price || null,
                description: d.description || '',
                location: d.location || '',
                lat: d.lat || null,
                lon: d.lon || null,
                md5: d.md5,
            })) : [],
            tags: course.tags ? JSON.parse(JSON.stringify(course.tags)) : [],
            images: course.images ? JSON.parse(JSON.stringify(course.images)) : [],
            files: course.files ? JSON.parse(JSON.stringify(course.files)) : [],
        };

        courseStore.updateKeyValue('newCourse', copiedCourse);

        appState.openDrawer('newCourse', {
            height: drawerHeightLarge,
            callback: () => {
                appState.toggleDrawer();
                this.loadAll();
            },
        }, drawerLevel + 1);
    }

    handleSignupSuccess = async () => {
        // Reload course data after successful signup
        await this.loadAll();
    }

    // Admin participant management methods
    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ search });

        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }

        if (!search || search.length < 2) {
            this.setState({ searchResults: [], searchLoading: false });
            return;
        }

        this.setState({ searchLoading: true });

        this.searchTimer = setTimeout(() => {
            this.doSearch(search);
        }, 300);
    }

    doSearch = async (search) => {
        const { userStore } = this.props.stores;
        const response = await userStore.searchUsersAll({ search });
        if (response?.status === 200) {
            this.setState({ searchResults: response.data || [], searchLoading: false });
        } else {
            this.setState({ searchResults: [], searchLoading: false });
        }
    }

    clearSearch = () => {
        this.setState({ search: '', searchResults: [] });
    }

    addParticipant = async (e) => {
        const userId = parseInt(e.currentTarget.dataset.id, 10);
        const dateMd5 = e.currentTarget.dataset.datemd5 || null;
        const { searchResults } = this.state;
        const user = searchResults.find(u => u.id === userId);

        if (!user) return;

        const { courseStore } = this.props.stores;
        await courseStore.adminAddParticipant(user, dateMd5);
        this.clearSearch();
        await this.loadAll();
    }

    removeParticipant = async (e) => {
        const md5 = e.currentTarget.dataset.md5;
        const field = e.currentTarget.dataset.field || 'participants';

        if (!md5) return;

        const { courseStore } = this.props.stores;
        await courseStore.adminRemoveParticipant(md5, field);
        await this.loadAll();
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.id !== this.props.id) {
            this.loadAll(nextProps);
        }
    }

    render() {
        const { showEdit, sections } = this.state;
        const { drawerLevel = 1 } = this.props;

        const { appState, userStore, courseStore } = this.props.stores;
        const { user = {}, isAdmin, isCustomerAdmin } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        const { course } = courseStore;

        if (!course || !course.id) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center py-5'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        const canEdit = isAdmin || isCustomerAdmin || course.user === user.id;
        const signupCount = countCourseSignups(course);

        const { examStore, examAttemptStore } = this.props.stores;
        const { exam = {} } = examStore;
        const { examAttempts = [] } = examAttemptStore;
        const participantExam = course.exam > 0 && exam.id === course.exam ? exam : null;
        const examAccess = courseExamAccess({ exam: participantExam, userId: user.id, attempts: examAttempts });

        return (<>
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100' style='max-width: 960px; padding-bottom: 100px;'>

                    {/* Header */}
                    <div class='d-flex justify-content-between align-items-start px-3 pt-3'>
                        <div>
                            <h4 class='mb-1'>{course.title}</h4>
                            {course.place && (
                                <p class='text-body-secondary mb-0'>
                                    <i class='fa-solid fa-location-dot me-1' />
                                    {course.place}
                                </p>
                            )}
                        </div>
                        {canEdit && (
                            <div class='d-flex gap-2'>
                                <button
                                    class='btn btn-outline-secondary btn-sm rounded-pill'
                                    onClick={this.copyCourse}
                                    title='Kopier kurs'
                                >
                                    <i class='fa-solid fa-copy me-1' />
                                    <Text id='course.copy'>Kopier</Text>
                                </button>
                                <button
                                    class='btn btn-outline-primary btn-sm rounded-pill'
                                    onClick={this.editCourse}
                                >
                                    <i class='fa-solid fa-pen me-1' />
                                    <Text id='course.edit'>Rediger</Text>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Images */}
                    {course.images?.length > 0 && (
                        <div class='px-3 py-3'>
                            <ImageScroller
                                stores={this.props.stores}
                                images={course.images}
                                showImg={img => localUtil.displayImg(img, this.props, '800x')}
                                showThumbs={true}
                            />
                        </div>
                    )}

                    {/* Info badges */}
                    <div class='px-3 mb-3'>
                        <div class='d-flex flex-wrap gap-2'>
                            {course.registrationIsOpen && (
                                <span class='badge text-bg-success'>
                                    <i class='fa-solid fa-door-open me-1' />
                                    <Text id='course.registrationOpen'>Påmelding åpen</Text>
                                </span>
                            )}
                            {course.price > 0 && (
                                <span class='badge text-bg-primary'>
                                    <i class='fa-solid fa-coins me-1' />
                                    {util.format(course.price, 0)} kr (inkl. mva)
                                </span>
                            )}
                            {course.dates?.length > 0 && (
                                <span class='badge text-bg-info'>
                                    <i class='fa-solid fa-calendar-days me-1' />
                                    {course.dates.length} datoer
                                </span>
                            )}
                            {signupCount > 0 && (
                                <span class='badge text-bg-secondary'>
                                    <i class='fa-solid fa-users me-1' />
                                    {signupCount} påmeldt
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Ingress */}
                    {course.ingress && (
                        <div class='px-3 mb-3'>
                            <p class='lead'>{course.ingress}</p>
                        </div>
                    )}

                    {/* Body */}
                    {course.body && (
                        <div class='px-3 mb-4'>
                            <Markdown markdown={course.body} />
                        </div>
                    )}

                    {/* Course-level signup card */}
                    <div class='px-3 mb-4'>
                        <CourseSignupCard
                            stores={this.props.stores}
                            course={course}
                            callback={this.handleSignupSuccess}
                        />
                    </div>

                    {/* Exam sent to this participant */}
                    {examAccess && (
                        <div class='px-3 mb-4'>
                            <div class={`p-3 rounded-3 bg-body border ${examAccess.status === COURSE_EXAM_STATUS.passed ? 'border-success' : examAccess.status === COURSE_EXAM_STATUS.open ? 'border-primary' : ''}`}>
                                <h5 class='mb-2'>
                                    <i class='fa-solid fa-bullseye me-2' />
                                    <Text id='course.examForYou'>Prøven er sendt til deg</Text>
                                </h5>
                                {participantExam.title && <p class='mb-2'>{participantExam.title}</p>}
                                {examAccess.deadline && (
                                    <p class={`small mb-2 ${examAccess.status === COURSE_EXAM_STATUS.overdue ? 'text-danger' : 'text-body-secondary'}`}>
                                        <i class='fa-solid fa-hourglass-half me-1' />
                                        <Text id='course.examDeadlineLabel'>Frist</Text>: {formatDateTime(examAccess.deadline)}
                                    </p>
                                )}
                                {examAccess.status === COURSE_EXAM_STATUS.passed && (
                                    <a class='btn btn-outline-success btn-sm rounded-pill' href='/exam'>
                                        <i class='fa-solid fa-circle-check me-1' />
                                        <Text id='course.examPassed'>Du har bestått. Se resultatet</Text>
                                    </a>
                                )}
                                {examAccess.status === COURSE_EXAM_STATUS.overdue && (
                                    <span class='badge text-bg-secondary'>
                                        <Text id='course.examOverdue'>Fristen er passert</Text>
                                    </span>
                                )}
                                {examAccess.status === COURSE_EXAM_STATUS.open && (
                                    <a class='btn btn-primary btn-sm rounded-pill' href={`/exam/take/${participantExam.id}`}>
                                        <i class='fa-solid fa-play me-1' />
                                        <Text id='course.takeExam'>Ta prøven</Text>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Dates section (schedule view) */}
                    {course.dates?.length > 0 && (
                        <div class='px-3 mb-4'>
                            <h5 class='mb-3'>
                                <i class='fa-solid fa-calendar-days me-2' />
                                <Text id='course.schedule'>Kursplan</Text>
                            </h5>
                            <DateSelector
                                stores={this.props.stores}
                                course={course}
                                callback={this.handleSignupSuccess}
                                scheduleOnly={true}
                            />
                        </div>
                    )}

                    {/* Location */}
                    {(course.address || course.place) && (
                        <div class='px-3 mb-4'>
                            <h5 class='mb-3'>
                                <i class='fa-solid fa-location-dot me-2' />
                                <Text id='course.location'>Lokasjon</Text>
                            </h5>
                            <div class='card'>
                                <div class='card-body'>
                                    {course.address && <p class='mb-1'>{course.address}</p>}
                                    {(course.postalcode || course.place) && (
                                        <p class='mb-0 text-body-secondary'>
                                            {course.postalcode} {course.place}
                                        </p>
                                    )}
                                    {course.country && (
                                        <p class='mb-0 text-body-secondary'>{course.country}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Files */}
                    {course.files?.length > 0 && (
                        <div class='px-3 mb-4'>
                            <h5 class='mb-3'>
                                <i class='fa-solid fa-files me-2' />
                                <Text id='course.files'>Filer</Text>
                            </h5>
                            <div class='list-group'>
                                {course.files.map(file => (
                                    <a
                                        href={file.s3Link || file.url}
                                        target='_blank'
                                        class='list-group-item list-group-item-action d-flex align-items-center'
                                    >
                                        <i class='fa-solid fa-file me-2' />
                                        {file.name || file.title || 'File'}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Tags */}
                    {course.tags?.length > 0 && (
                        <div class='px-3 mb-4'>
                            <div class='d-flex flex-wrap gap-1'>
                                {course.tags.map(tag => (
                                    <span class='badge text-bg-light'>#{tag.name}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Exam after the course */}
                    {canEdit && (
                        <div class='px-3 mb-4'>
                            <div class='p-3 rounded-3 bg-body border'>
                                <h5 class='mb-2'>
                                    <i class='fa-solid fa-bullseye me-2' />
                                    <Text id='course.exam'>Prøve etter kurset</Text>
                                </h5>
                                {course.exam > 0 ? (
                                    <div class='d-flex flex-wrap gap-2 align-items-center'>
                                        <button
                                            type='button'
                                            class='btn btn-primary btn-sm rounded-pill'
                                            onClick={this.sendExam}
                                            disabled={signupCount === 0}
                                            title={signupCount === 0 ? 'Kurset har ingen deltakere ennå' : ''}
                                        >
                                            <i class='fa-solid fa-paper-plane me-1' />
                                            <Text id='course.sendExam'>Send prøve til deltakerne</Text>
                                        </button>
                                        <a
                                            class='btn btn-outline-primary btn-sm rounded-pill'
                                            href={`/exam/results/${course.exam}/${course.id}`}
                                        >
                                            <i class='fa-solid fa-table-list me-1' />
                                            <Text id='course.examResults'>Resultater</Text>
                                        </a>
                                        <small class='text-body-secondary'>
                                            <Text id='course.sendExamHelp'>Deltakerne får e-post og varsel med frist. De som består får et signert diplom.</Text>
                                        </small>
                                    </div>
                                ) : (
                                    <small class='text-body-secondary'>
                                        <Text id='course.noExamLinked'>Ingen prøve er koblet til kurset. Velg en under «Rediger».</Text>
                                    </small>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Admin participant management */}
                    {canEdit && (
                        <div class='px-3 mb-4'>
                            <div class='p-3 rounded-3 bg-body border border-danger'>
                                <h5 class='text-danger mb-3'>
                                    <i class='fa-solid fa-user-gear me-2' />
                                    Administrer deltakere
                                </h5>

                                {/* Search input */}
                                <div class='input-group mb-3'>
                                    <span class='input-group-text'>
                                        <i class='fa-regular fa-magnifying-glass' />
                                    </span>
                                    <input
                                        type='text'
                                        class='form-control'
                                        placeholder='Søk etter bruker (navn eller e-post)...'
                                        value={this.state.search}
                                        onInput={this.searchInput}
                                    />
                                    {this.state.search && (
                                        <button
                                            type='button'
                                            class='btn btn-outline-secondary'
                                            onClick={this.clearSearch}
                                        >
                                            <i class='fa-solid fa-times' />
                                        </button>
                                    )}
                                </div>

                                {/* Search loading */}
                                {this.state.searchLoading && (
                                    <div class='text-center py-2'>
                                        <div class='spinner-border spinner-border-sm text-primary' role='status'>
                                            <span class='visually-hidden'>Søker...</span>
                                        </div>
                                    </div>
                                )}

                                {/* Search results dropdown */}
                                {this.state.searchResults.length > 0 && (
                                    <div class='list-group mb-3 shadow-sm'>
                                        {this.state.searchResults.slice(0, 10).map(searchUser => (
                                            <button
                                                type='button'
                                                class='list-group-item list-group-item-action d-flex justify-content-between align-items-center'
                                                onClick={this.addParticipant}
                                                data-id={searchUser.id}
                                            >
                                                <div>
                                                    <strong>{searchUser.firstname} {searchUser.lastname}</strong>
                                                    <small class='text-body-secondary ms-2'>{searchUser.email}</small>
                                                </div>
                                                <i class='fa-solid fa-plus text-success' />
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Course-level participants */}
                                <h6 class='mt-4'>
                                    Deltakere (kursnivå)
                                    <span class='badge text-bg-secondary ms-2'>{course.participants?.length || 0}</span>
                                </h6>
                                {(course.participants?.length > 0) ? (
                                    <div class='table-responsive'>
                                        <table class='table table-sm table-hover'>
                                            <thead>
                                                <tr>
                                                    <th>Navn</th>
                                                    <th>E-post</th>
                                                    <th>Telefon</th>
                                                    <th>Påmeldt</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {course.participants.map(p => (
                                                    <tr>
                                                        <td>
                                                            {p.firstname} {p.lastname}
                                                            {p.addedByAdmin && (
                                                                <span class='badge text-bg-warning ms-1' title='Lagt til av admin'>
                                                                    <i class='fa-solid fa-user-shield' />
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>{p.email}</td>
                                                        <td>{p.cellphone}</td>
                                                        <td>{p.signupDate ? util.formatDate(p.signupDate, 'datetime') : ''}</td>
                                                        <td>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-outline-danger'
                                                                onClick={this.removeParticipant}
                                                                data-md5={p.md5}
                                                                data-field='participants'
                                                                title='Fjern deltaker'
                                                            >
                                                                <i class='fa-solid fa-trash' />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p class='text-body-secondary mb-0'>Ingen deltakere ennå</p>
                                )}

                                {/* Date-specific participants */}
                                {course.dates?.map((date, idx) => {
                                    const dateNumber = idx + 1;
                                    const participantsFieldName = `participants${dateNumber}`;
                                    const dateParticipants = course[participantsFieldName] || [];

                                    return (
                                        <div class='mt-4' key={date.md5}>
                                            <h6>
                                                {date.title || util.formatDate(date.startTime, 'datetime')}
                                                <span class='badge text-bg-secondary ms-2'>{dateParticipants.length}</span>
                                                {date.slots > 0 && (
                                                    <span class='badge text-bg-info ms-1'>
                                                        {dateParticipants.length}/{date.slots} plasser
                                                    </span>
                                                )}
                                            </h6>

                                            {/* Search to add to specific date */}
                                            {this.state.search && this.state.searchResults.length > 0 && (
                                                <div class='list-group mb-2 shadow-sm'>
                                                    {this.state.searchResults.slice(0, 5).map(searchUser => (
                                                        <button
                                                            type='button'
                                                            class='list-group-item list-group-item-action d-flex justify-content-between align-items-center py-1'
                                                            onClick={this.addParticipant}
                                                            data-id={searchUser.id}
                                                            data-datemd5={date.md5}
                                                        >
                                                            <small>
                                                                <strong>{searchUser.firstname} {searchUser.lastname}</strong>
                                                                <span class='text-body-secondary ms-1'>{searchUser.email}</span>
                                                            </small>
                                                            <i class='fa-solid fa-plus text-success' />
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {dateParticipants.length > 0 ? (
                                                <div class='table-responsive'>
                                                    <table class='table table-sm table-hover'>
                                                        <thead>
                                                            <tr>
                                                                <th>Navn</th>
                                                                <th>E-post</th>
                                                                <th>Telefon</th>
                                                                <th>Påmeldt</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {dateParticipants.map(p => (
                                                                <tr>
                                                                    <td>
                                                                        {p.firstname} {p.lastname}
                                                                        {p.addedByAdmin && (
                                                                            <span class='badge text-bg-warning ms-1' title='Lagt til av admin'>
                                                                                <i class='fa-solid fa-user-shield' />
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                    <td>{p.email}</td>
                                                                    <td>{p.cellphone}</td>
                                                                    <td>{p.signupDate ? util.formatDate(p.signupDate, 'datetime') : ''}</td>
                                                                    <td>
                                                                        <button
                                                                            type='button'
                                                                            class='btn btn-sm btn-outline-danger'
                                                                            onClick={this.removeParticipant}
                                                                            data-md5={p.md5}
                                                                            data-field={participantsFieldName}
                                                                            title='Fjern deltaker'
                                                                        >
                                                                            <i class='fa-solid fa-trash' />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <p class='text-body-secondary small mb-0'>Ingen deltakere</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </>);
    }
}

export default CourseView;
